import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Divider,
  Grid,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import CourierLayout from '../CourierLayout';
import { API } from '../../../config';

const EmergencyAssistance = () => {
  const [location, setLocation] = useState('');
  const [issue, setIssue] = useState('');
  const [contact, setContact] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState('');

  const token =
    localStorage.getItem('courier_token') ||
    localStorage.getItem('token');

  const handleSubmit = async (event) => {
    event.preventDefault();
    setMessage('');

    if (!issue.trim()) {
      setMessage('Please describe the issue before submitting your request.');
      return;
    }

    if (!contact.trim()) {
      setMessage('Please provide a phone number or email for follow-up.');
      return;
    }

    try {
      setSubmitting(true);

      const response = await fetch(`${API}/api/courier/support-request`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          location: location.trim(),
          issue: issue.trim(),
          contact: contact.trim(),
          category: 'support',
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Unable to submit support request.');
      }

      setMessage('Support request submitted successfully.');
      setLocation('');
      setIssue('');
      setContact('');
    } catch (error) {
      console.error('Support request failed:', error);
      setMessage(error.message || 'Unable to submit support request.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <CourierLayout>
      <Box sx={{ mb: 3 }}>
        <Typography
          variant="h4"
          sx={{
            fontSize: '2rem',
            fontWeight: 700,
            lineHeight: 1.25,
            color: '#0f172a',
            mb: 1,
            textAlign: 'center',
          }}
        >
          Support Request
        </Typography>

        <Typography
          variant="body2"
          sx={{
            color: '#64748b',
            textAlign: 'center',
            fontSize: '0.95rem',
            lineHeight: 1.5,
          }}
        >
          Submit a request for help with loads, account access, payments, or platform issues.
        </Typography>
      </Box>

      <Paper
        elevation={1}
        sx={{
          p: { xs: 2, md: 2.5 },
          borderRadius: 3,
          border: '1px solid rgba(15, 23, 42, 0.08)',
          backgroundColor: '#ffffff',
        }}
      >
        <Paper
          elevation={0}
          sx={{
            p: { xs: 2, md: 2.5 },
            mb: 2,
            borderRadius: 3,
            backgroundColor: '#f8fafc',
            border: '1px solid #e2e8f0',
            textAlign: 'center',
          }}
        >
          <Typography
            variant="body2"
            sx={{
              color: '#64748b',
              fontSize: '0.95rem',
              lineHeight: 1.5,
            }}
          >
            For urgent support, contact{' '}
            <Box
              component="span"
              sx={{
                color: '#1976d2',
                fontWeight: 700,
              }}
            >
              support@hagglefreight.com
            </Box>
          </Typography>
        </Paper>

        {message && (
          <Alert
            severity={message.includes('successfully') ? 'success' : 'error'}
            sx={{ mb: 2, borderRadius: 2 }}
          >
            {message}
          </Alert>
        )}

        <form onSubmit={handleSubmit}>
          <Typography
            variant="subtitle1"
            sx={{
              color: '#0f172a',
              fontWeight: 700,
              mb: 1,
            }}
          >
            Request Details
          </Typography>

          <Divider sx={{ mb: 2 }} />

          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                label="Current Location"
                placeholder="City, state, or current location"
                fullWidth
                value={location}
                onChange={(event) => setLocation(event.target.value)}
                disabled={submitting}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                label="Issue Details"
                placeholder="Describe the issue"
                fullWidth
                multiline
                minRows={4}
                value={issue}
                onChange={(event) => setIssue(event.target.value)}
                disabled={submitting}
                required
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                label="Preferred Contact"
                placeholder="Phone number or email"
                fullWidth
                value={contact}
                onChange={(event) => setContact(event.target.value)}
                disabled={submitting}
                required
              />
            </Grid>

            <Grid item xs={12}>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: { xs: 'stretch', sm: 'flex-end' },
                  mt: 1,
                }}
              >
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  disabled={submitting}
                  sx={{
                    minHeight: 42,
                    px: 4,
                    textTransform: 'none',
                    fontWeight: 700,
                    borderRadius: 2,
                    width: { xs: '100%', sm: 'auto' },
                  }}
                >
                  {submitting ? 'Submitting...' : 'Submit Request'}
                </Button>
              </Box>
            </Grid>
          </Grid>
        </form>
      </Paper>
    </CourierLayout>
  );
};

export default EmergencyAssistance;